import classNames from "classnames";
import React from "react";
import "./kuika-loader.scss";

export type KuikaLoaderVariant = "ring" | "dots" | "logo";
export type KuikaLoaderSize = "sm" | "md" | "lg";

export interface IKuikaLoaderProps {
  /** `ring` (default) for a wait inside a control or row, `dots` inside a line of text, `logo` where the Kuika logo spinner stood. */
  variant?: KuikaLoaderVariant;
  /** ring / dots: the control icon ladder (14 / 16 / 18). logo: the control height ladder (24 / 32 / 40). */
  size?: KuikaLoaderSize;
  /** What assistive tech hears. Not shown. */
  label?: string;
  /** For a loader inside something that already announces the wait (a button's status, a toast): hidden from assistive tech. */
  decorative?: boolean;
  /** `aria-busy` on the loader itself, for callers that toggle a loader instead of unmounting it. */
  busy?: boolean;
  className?: string;
  "data-testid"?: string;
}

const DOT_KEYS: readonly string[] = ["first", "second", "third"];
const LOGO_MARK_SRC: string = "/images/common/kuika-logo-inside.svg";

/**
 * The designer's small loading indicator.
 *
 * - `ring`: the one inline spinner. `.kuika-button__spinner` and `KuikaSmallSpinner` draw the same
 *   ring (`_kuika-loader-ring.scss`).
 * - `dots`: three dots on the text baseline, as wide as the ring.
 * - `logo`: the Kuika logo spinner at control size. The full-screen and wrapper spinners stay
 *   `KuikaSpinner` / `KuikaSpinnerSmall`.
 *
 * Colour is `currentColor`. The root is a `role="status"` live region named by `label`; pass
 * `decorative` when the owner already announces. Under reduced motion nothing turns or lifts: the
 * ring and logo breathe (kuika-pulse), the dots only brighten.
 */
const KuikaLoader = ({
  variant = "ring",
  size = "md",
  label = "Loading",
  decorative = false,
  busy,
  className,
  "data-testid": dataTestId = "kuikaLoader"
}: IKuikaLoaderProps): JSX.Element => (
  <span
    className={classNames("kuika-loader", `kuika-loader--${variant}`, `kuika-loader--${size}`, className)}
    role={decorative ? undefined : "status"}
    aria-live={decorative ? undefined : "polite"}
    aria-label={decorative ? undefined : label}
    aria-hidden={decorative ? true : undefined}
    aria-busy={busy}
    data-testid={dataTestId}
  >
    {variant === "dots" &&
      DOT_KEYS.map((key: string): JSX.Element => <span key={key} className="kuika-loader__dot" aria-hidden="true" />)}
    {variant === "logo" && (
      <>
        <span className="kuika-loader__logo-square" aria-hidden="true" />
        <img className="kuika-loader__logo-mark" src={LOGO_MARK_SRC} alt="" aria-hidden="true" />
      </>
    )}
  </span>
);

export default KuikaLoader;
